import React from "react";

interface AddEditFactoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingFactory: Record<string, any> | null;
  formData: {
    name: string;
    address_line_1: string;
    address_line_2: string;
    suite: string;
    city: string;
    state_province: string;
    country: string;
    postal_code: string;
    telephone: string;
    email: string;
    status: "active" | "inactive";
  };
  formErrors: Record<string, string>;
  saving: boolean;
  handleInputChange: (field: string, value: string) => void;
  handleSaveFactory: () => void;
  onAddFactory?: (factory: any) => void; // ✅ optional prop for AskRexi extracted factories
}

const AddEditFactoryModal = (props: AddEditFactoryModalProps) => {
  const {
    isOpen,
    onClose,
    editingFactory,
    formData,
    formErrors,
    saving,
    handleInputChange,
    handleSaveFactory,
    onAddFactory,
  } = props;

  if (!isOpen) return null;

  return (
    <div className="data-modal-overlay" onClick={onClose}>
      <div className="data-modal" onClick={(e) => e.stopPropagation()}>
        <div className="data-modal-header">
          <h2 className="data-modal-title">
            {editingFactory ? "Factory Details" : "Add New Factory"}
          </h2>
          <button className="modal-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="data-modal-body">
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                Factory Name <span className="required-indicator">*</span>
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter factory name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
              />
              {formErrors.name && (
                <div className="form-error">{formErrors.name}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                Address Line 1 <span className="required-indicator">*</span>
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="Street address"
                value={formData.address_line_1}
                onChange={(e) =>
                  handleInputChange("address_line_1", e.target.value)
                }
              />
              {formErrors.address_line_1 && (
                <div className="form-error">{formErrors.address_line_1}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Address Line 2</label>
              <input
                type="text"
                className="form-input"
                placeholder="Apartment, suite, etc."
                value={formData.address_line_2}
                onChange={(e) =>
                  handleInputChange("address_line_2", e.target.value)
                }
              />
            </div>

            <div className="form-group">
              <label className="form-label">Suite/Building</label>
              <input
                type="text"
                className="form-input"
                placeholder="Building, floor, zone"
                value={formData.suite}
                onChange={(e) => handleInputChange("suite", e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                City <span className="required-indicator">*</span>
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="City"
                value={formData.city}
                onChange={(e) => handleInputChange("city", e.target.value)}
              />
              {formErrors.city && (
                <div className="form-error">{formErrors.city}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                State/Province <span className="required-indicator">*</span>
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="State or province"
                value={formData.state_province}
                onChange={(e) =>
                  handleInputChange("state_province", e.target.value)
                }
              />
              {formErrors.state_province && (
                <div className="form-error">{formErrors.state_province}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                Country <span className="required-indicator">*</span>
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="Country"
                value={formData.country}
                onChange={(e) => handleInputChange("country", e.target.value)}
              />
              {formErrors.country && (
                <div className="form-error">{formErrors.country}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Postal Code</label>
              <input
                type="text"
                className="form-input"
                placeholder="ZIP or postal code"
                value={formData.postal_code}
                onChange={(e) =>
                  handleInputChange("postal_code", e.target.value)
                }
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Phone <span className="required-indicator">*</span>
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="+1-555-123-4567"
                value={formData.telephone}
                onChange={(e) => handleInputChange("telephone", e.target.value)}
              />
              {formErrors.telephone && (
                <div className="form-error">{formErrors.telephone}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                Email <span className="required-indicator">*</span>
              </label>
              <input
                type="email"
                className="form-input"
                placeholder="contact@factory.com"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
              />
              {formErrors.email && (
                <div className="form-error">{formErrors.email}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={formData.status}
                onChange={(e) => handleInputChange("status", e.target.value)}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        <div className="data-modal-footer">
          <button
            className="modal-btn modal-btn-cancel"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="modal-btn modal-btn-save"
            onClick={async () => {
              await handleSaveFactory();
              if (onAddFactory && formData.name?.trim()) {
                onAddFactory({
                  name: formData.name,
                  address_line_1: formData.address_line_1,
                  city: formData.city,
                  country: formData.country,
                  source: "user",
                });
              }
              onClose();
            }}
            disabled={saving}
          >
            {saving
              ? "Saving..."
              : editingFactory
              ? "Update Factory"
              : "Add Factory"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditFactoryModal;
